import {
  ViewerConfig,
  RendererTypes,
  CoursewareStructure,
  ENVIRONMENTS,
  IAnnotation,
  setAppConfigOnWindowForTrackers,
} from "@bronte/viewer/src/main";
import getAppConfig from "../src/config/appConfig";
import { generateCSS } from "./shared/utils";
import {
  AnnotationsAPI,
  SpotlightAPI,
  SearchAPI,
  MediaAPI,
  SelectedThemePayload,
  Spotlight,
  ThemeOptions,
  ThemeVariants,
  TOCStitchedLessonMapItem,
} from "./shared/types";
import Globals from "@bronte/offline-services/src/utils/GlobalConstants";
import { getEncryptedFilesList } from "@bronte/offline-services/src/utils/DataUtils";
import { IAppTrackerConfig } from "@bronte/viewer";
import { debounce } from "lodash";

declare global {
  interface Window {
    appTrackerConfig: IAppTrackerConfig;
  }
}
export class ViewerApi {
  #viewer: any;
  #appConfig!: ReturnType<typeof getAppConfig>;
  #selectedThemePayload!: SelectedThemePayload;
  #viewerConfig: ViewerConfig;
  private webStitchedLessonTocMap: Map<string, TOCStitchedLessonMapItem>;

  constructor(viewerConfig: ViewerConfig) {
    this.#viewerConfig = viewerConfig;
    this.webStitchedLessonTocMap = new Map([]);

    if (!this.#validate()) {
      // todo: return error codes and messages
      console.error("Invalid initialization of Bronte Viewer.");
      return;
    }

    this.#init();
  }

  /**
   * todo: more validations
   * @returns
   */
  #validate = (): boolean => {
    if (this.#viewerConfig.renderer === RendererTypes.MOBILE && !this.#viewerConfig.packagePath) {
      // package path is required
      return false;
    }

    return true;
  };

  #init = async (): Promise<void> => {
    // TODO: Refactor the code to remove references of RendererTypes in code. (BRNT-21994)
    if (this.#isLiveWeb()) {
      this.#appConfig = getAppConfig(this.#viewerConfig.env ?? ENVIRONMENTS.STG);
      setAppConfigOnWindowForTrackers(this.#appConfig);
      console.log("ViewerApi: Config loaded:", this.#appConfig);
    }

    // initialize the viewer after the config is loaded
    await import("@bronte/viewer/src/Viewer").then(async ({ Viewer }) => {
      this.#viewer = new Viewer(
        this.#viewerConfig,
        this.#appConfig?.getLearnerGraphqlEndpoint(),
        this.#appConfig?.getLearnerWebsocketURL(),
        this.#appConfig?.getCSGURL(),
        this.#appConfig?.getLaunchDarklyClientId(),
      );
    });
  };

  /**
   * If the Bronte Viewer is initialized w/o package path and renderer is WEB, then its a live WEB which connects to learnspace
   *
   * @returns true or false
   */
  #isLiveWeb = (): boolean =>
    !this.#viewerConfig.packagePath && this.#viewerConfig.renderer === RendererTypes.WEB;

  getCoursewareStructure = (): CoursewareStructure => {
    return this.#isLiveWeb()
      ? this.#viewer?.services.coursewareStructureService.getCoursewareStructure()
      : window.ambrosia?.toc;
  };

  // TODO: This method will be removed after implementation of BRNT-29613
  mapWebStitchedLessonToc = (
    stitchedLessonTocMap: Map<string, TOCStitchedLessonMapItem> | undefined,
  ): void => {
    if (stitchedLessonTocMap) {
      this.webStitchedLessonTocMap = stitchedLessonTocMap;
    }
  };

  getEncryptedFilesList = (elementId: string): string[] => getEncryptedFilesList(elementId);

  // TODO : Need to remove screenId from subTarget after full web toc implementation
  #debouncedRoute = (
    elementId: string,
    subTarget: {
      annotation?: IAnnotation;
      dataId?: string;
      text?: string;
      screenId?: string;
    } = {},
  ): void => {
    /**
     * @param elementToRoute can be chapter, lesson or screen.
     * @param screenId in case elementToRoute is the lesson id.
     */

    const navigateToRoute = (elementToRoute: string, screenId?: string): void => {
      /**
       * elementId can be the chapter id, lesson id, screenId.
       * subTarget may contains information like scroll to particular screen, annotation or page number while navigating to the given route.
       * Persisting this information to apply the scroll effect later once content is loaded on the screen.
       */
      this.#viewer.pendingRouteComplete = {};
      this.#viewer.clientData.currentRoute = {
        elementId: elementToRoute,
        subTarget: {
          ...subTarget,
          ...(screenId && { screenId }), // saving screen id in case screen id is part of stitched lesson.
        },
        isLoaded: false,
      };

      this.#viewer.services.cdpServices
        .get("ROUTE_SERVICE")
        .build({
          target: {
            id: elementToRoute,
            subTarget: screenId && { screenId }, // Stitched lesson will use it to load this screen first before all other screens.
          },
        })
        .routeTo();
    };

    if (this.#isLiveWeb()) {
      // TODO: The code here needs to change after the full web toc implementation. Need to leverage the code for offline BRNT-29613
      let screenId;
      // @ts-ignore:next-line
      const tocElement: TOCStitchedLessonMapItem | undefined =
        subTarget?.screenId && this.webStitchedLessonTocMap.get(subTarget.screenId);
      const stitchedElementToRoute = tocElement?.elementToRoute || elementId;
      if (stitchedElementToRoute !== this.#viewer.clientData?.currentRoute?.elementId) {
        // Reaching here means it'a new route, load it.
        if (tocElement?.isScreen && stitchedElementToRoute !== subTarget?.screenId) {
          // It means elementId is a screen inside stitched lesson.
          screenId = subTarget?.screenId;
        }
        return navigateToRoute(stitchedElementToRoute, screenId);
      }

      if (tocElement?.isScreen) {
        // Reaching here means it's a new screen in the same stitched lesson then scroll to it.

        this.#viewer.services.cdpServices.get("EVENT_SERVICE").build().publish("scrollTo", {
          screenId: subTarget.screenId,
          scrollIntoView: true,
        });

        return;
      }
    }

    if (subTarget?.annotation && elementId !== subTarget?.annotation?.screenId) {
      console.error("Annotation doesn't exist on the given route (elementId).");
    }

    const tocElement = Globals.tocMap.get(elementId);

    if (!tocElement) {
      throw new Error(`The element id ${elementId} doesn't exist.`);
    }

    const elementToRoute = tocElement.elementToRoute;

    if (elementToRoute !== this.#viewer.clientData?.currentRoute?.elementId) {
      // Reaching here means it'a new route, load it.
      let screenId = "";
      if (tocElement.isScreen && elementToRoute !== elementId) {
        // It means elementId is a screen inside stitched lesson.
        screenId = elementId;
      }
      return navigateToRoute(elementToRoute, screenId);
    }

    const eventService = this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();

    if (subTarget?.annotation) {
      eventService.publish(
        `annotations.scroll.event.${subTarget.annotation.elementId}`,
        subTarget.annotation,
      );

      return;
    }

    if (subTarget?.dataId) {
      eventService.publish(`scroll.to.event`, {
        dataId: subTarget.dataId,
        text: subTarget.text,
      });
      return;
    }

    if (tocElement.isScreen) {
      // Reaching here means it's a new screen in the same stitched lesson then scroll to it.

      eventService.publish("scrollTo", {
        screenId: elementId,
        scrollIntoView: true,
      });

      return;
    }
  };

  route = debounce(this.#debouncedRoute, 500, {
    leading: true,
    trailing: false,
  });

  getThemeVariants = async (): Promise<ThemeVariants> => {
    if (!this.#selectedThemePayload) {
      const themeService = this.#viewer.services.cdpServices.get("THEME_SERVICE").build({
        target: { id: this.#viewer.coursewareItemId },
      });

      this.#selectedThemePayload = await themeService.getThemeVariants();
    }

    return this.#selectedThemePayload?.themeVariants || {};
  };

  updateTheme = async (themeOptions: ThemeOptions): Promise<void> => {
    if (!this.#selectedThemePayload) {
      await this.getThemeVariants();
    }

    if (this.#selectedThemePayload?.themeVariants) {
      const themeService = this.#viewer.services.cdpServices.get("THEME_SERVICE").build({
        target: { id: this.#viewer.coursewareItemId },
      });

      let selectedThemeVariant = await themeService.getThemeVariant(
        themeOptions.variantId,
        this.#selectedThemePayload.themeId || this.#selectedThemePayload.id, // id and themeId are the same but id is returned in offline and themeId is returned in web.
      );

      if (selectedThemeVariant) {
        const variantName = selectedThemeVariant.variantName;
        selectedThemeVariant = JSON.parse(selectedThemeVariant.config);

        selectedThemeVariant.css = selectedThemeVariant.css + generateCSS(themeOptions);
        selectedThemeVariant.variantName = variantName;
        selectedThemeVariant.continuousScroll = themeOptions.continuousScroll;
        await themeService.setInTheme(selectedThemeVariant);
      } else {
        console.error("Specified theme variant id not found.");
      }
    }
  };

  // media api to handle controls of mmi player (like play/pause) from bronte viewer api
  media = (): MediaAPI => {
    const eventService = this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();
    return {
      play: (): void => {
        // TODO: Noop right now. media.play.event will be added later.
      },
      pause: (): void => {
        // this event is subscribed by bronte mmi component and will pause all the audio/video players on the screen if playing
        eventService.publish(`media.pause.event`);
      },
    };
  };

  annotations = (): AnnotationsAPI => {
    const eventService = this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();

    return {
      load: (annotations: IAnnotation[], options?: { hideAnnotations?: boolean }): void => {
        /**
         * Update proxy watch object to trigger on annotation change subscription
         * changes to this object are listened by annotation change subscription in viewer.ts
         */
        this.#viewer.clientDataProxy.annotations = annotations;

        // We should check if hideAnnotations property is exist in options object and should not change persisted data if not exist, because it could trigger unwanted change of hide annotations setting.
        if (options?.hideAnnotations !== undefined) {
          this.#viewer.clientData.hideAnnotations = options?.hideAnnotations;
        }
      },
      add: (annotation: IAnnotation): void => {
        eventService.publish(`annotations.add.event.${annotation.elementId}`, annotation);
      },
      edit: (annotation: IAnnotation): void => {
        eventService.publish(`annotations.edit.event.${annotation.elementId}`, annotation);
      },
      delete: (annotation: IAnnotation): void => {
        eventService.publish(`annotations.delete.event.${annotation.elementId}`, annotation);
      },
      show: (showAnnotations = true): void => {
        this.#viewer.clientData.hideAnnotations = !showAnnotations;
        eventService.publish(showAnnotations ? "annotations.show.event" : "annotations.hide.event");
      },
      clearSelection: (): void => {
        eventService.publish("annotations.clear.selection.event");
      },
    };
  };

  spotlight = (): SpotlightAPI => {
    const eventService = this.#viewer.services.cdpServices.get("EVENT_SERVICE").build();

    return {
      apply: (options: Spotlight): void => {
        if (options?.selector && options?.mark) {
          eventService.publish("spotlight.apply.event", options);
        }
      },
      clear: (): void => {
        if (this.#viewer.clientData?.currentRoute?.subTarget) {
          this.#viewer.clientData.currentRoute.subTarget = {};
        }

        eventService.publish("spotlight.clear.event");
      },
    };
  };

  restart = (parentActivityId: string | undefined): void => {
    if (!parentActivityId) {
      console.error("Unable to reset the course.");
      return;
    }

    this.#viewer.services.cdpServices
      .get("LEARNER_SERVICE")
      .build({
        id: parentActivityId,
      })
      .restart(parentActivityId);
  };

  search = (): SearchAPI => {
    return {
      clear: (): void => {
        this.spotlight().clear();
      },
    };
  };

  onNetworkStatusChange = (isOnline: boolean): void => {
    this.#viewer.services.cdpServices.get("CLASS_SERVICE").build().onNetworkStatusChange(isOnline);
  };
}
